/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { toast } from 'react-hot-toast';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RecordPayment from './RecordPayment';
import EditPayment from './EditPayment';
import { getUserDisplayName } from '../../utils/userUtils';
import { calculateDays } from '../../utils/invoiceCalculations';
import { formatRate } from '../../utils/ratePricing';

// Format a calendar-date field (billing period, usage start/end) in UTC so the
// displayed day matches exactly what was stored, with no timezone shift.
const fmtUTCDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
};

// Helper function to safely display user name (handles deleted users)
const getPaymentUserName = (user) => {
  if (!user) return 'N/A';
  if (user.first_name) {
    return getUserDisplayName(user);
  }
  return user.email || `User ID: ${user}`;
};

// Helper function to extract invoice number from invoice_id
// e.g., "202601270728_INV011" -> 11
const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = invoiceId.split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

const formatRateBasis = (basis) => {
  if (!basis) return '/ day';
  return `/ ${basis}`;
};

const InvoiceDetail = ({ open, onClose, invoiceId, selectedInvoice, onUpdate }) => {
  const [invoice, setInvoice] = useState(selectedInvoice || null);
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [deletePaymentConfirmOpen, setDeletePaymentConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(invoiceId);
  
  // Define fetchInvoiceDetails outside of useEffect to fix dependency issue
  const fetchInvoiceDetails = async (invoiceIdToFetch, invoiceIdString) => {
    try {
      setLoading(true);
      
      // If we have a specific invoice ID, fetch its details with chamber/resource usage
      const idToUse = invoiceIdToFetch || selectedInvoiceId;
      const invoiceIdStr = invoiceIdString || selectedInvoice?.invoice_id;
      
      if (idToUse && invoiceIdStr) {
        const response = await axios.get(`/api/invoices/${idToUse}?populate=true&invoice_id=${invoiceIdStr}`);
        setInvoice(response.data);
        
        // Also fetch all invoices for the project dropdown
        // Prefer MongoDB ObjectId (_id) over project_id string for API call
        const projectObjectId = response.data?.project?._id || 
                                response.data?.projectInfo?._id || 
                                selectedInvoice?.projectInfo?._id ||
                                selectedInvoice?.project?._id;
        const projectIdString = response.data?.project?.project_id || 
                                response.data?.projectInfo?.project_id || 
                                selectedInvoice?.projectInfo?.project_id || 
                                selectedInvoice?.project_id;
        const projectIdForQuery = projectObjectId || projectIdString;
        if (projectIdForQuery) {
          const invoicesResponse = await axios.get(`/api/invoices/by-project/${projectIdForQuery}`);
          setAllInvoices(invoicesResponse.data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      toast.error('Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch invoice details when dialog opens - ALWAYS fetch to get chamber/resource usage
  useEffect(() => {
    if (open && selectedInvoice) {
      setSelectedInvoiceId(selectedInvoice._id);
      fetchInvoiceDetails(selectedInvoice._id, selectedInvoice.invoice_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedInvoice]);

  const handleInvoiceChange = async (event) => {
    const newInvoiceId = event.target.value;
    setSelectedInvoiceId(newInvoiceId);
    setLoading(true);
    try {
      const selectedInvoice = allInvoices.find(inv => inv._id === newInvoiceId);
      const response = await axios.get(`/api/invoices/${newInvoiceId}?populate=true&invoice_id=${selectedInvoice.invoice_id}`);
      setInvoice(response.data);
    } catch (error) {
      console.error('Error fetching selected invoice:', error);
      toast.error('Failed to load selected invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentRecorded = (updatedInvoice) => {
    setInvoice(updatedInvoice);
    if (onUpdate) {
      onUpdate(updatedInvoice);
    }
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    setEditPaymentOpen(true);
  };

  const handleConfirmDeletePayment = async () => {
    if (!invoice || !paymentToDelete) return;
    setIsDeletingPayment(true);
    try {
      await axios.delete(
        `/api/invoices/${invoice._id || invoice.parentId}/payment/${paymentToDelete._id}?invoice_id=${invoice.invoice_id}`
      );
      toast.success('Payment deleted successfully');
      setDeletePaymentConfirmOpen(false);
      setPaymentToDelete(null);
      await fetchInvoiceDetails(invoice._id || invoice.parentId, invoice.invoice_id);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsDeletingPayment(false);
    }
  };

  const handlePaymentUpdated = async (updatedInvoiceData) => {
    // Re-fetch the full invoice details to get chamber/resource usage, populated users, etc.
    await fetchInvoiceDetails(invoice._id || invoice.parentId, invoice.invoice_id);
    if (onUpdate) {
      onUpdate(updatedInvoiceData);
    }
  };

  const handleViewPDF = async () => {
    try {
      const response = await axios.get(`/api/invoices/${invoiceId}/pdf?invoice_id=${invoice.invoice_id}`, {
        responseType: 'blob'
      });
      
      // Create a blob URL for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`/api/invoices/${invoiceId}/pdf?invoice_id=${invoice.invoice_id}`, {
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header if available
      let filename = `invoice-${getInvoiceNumber(invoice.invoice_id)}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        // Parse filename from header: attachment; filename="custom_name.pdf"
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(?:(?:"([^"]*)")|([^;\n]*))/i);
        if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
          filename = filenameMatch[1] || filenameMatch[2];
        }
      }
      
      // Create a blob URL for the PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Helper function to get current status from history
  const getCurrentStatus = () => {
    if (!invoice || !invoice.status_history || invoice.status_history.length === 0) {
      return 'Unknown';
    }
    return invoice.status_history[invoice.status_history.length - 1].status;
  };

  // Helper function to determine status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partially Paid':
        return 'warning';
      case 'Overdue':
        return 'error';
      case 'Sent to Client':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        aria-labelledby="invoice-detail-title"
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle id="invoice-detail-title">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">
                Invoice Details
              </Typography>
              {allInvoices.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    value={selectedInvoiceId}
                    onChange={handleInvoiceChange}
                    displayEmpty
                  >
                    {allInvoices.map((inv) => (
                      <MenuItem key={inv._id} value={inv._id}>
                        Invoice #{getInvoiceNumber(inv.invoice_id)} - {new Date(inv.generation_date).toLocaleDateString()}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            <Box>
              <IconButton aria-label="View details" onClick={handleViewPDF} title="View PDF">
                <VisibilityIcon />
              </IconButton>
              <IconButton aria-label="Download" onClick={handleDownloadPDF} title="Download PDF">
                <DownloadIcon />
              </IconButton>
              <IconButton aria-label="View invoice" 
                onClick={() => setRecordPaymentOpen(true)} 
                title="Record Payment"
                color="primary"
              >
                <ReceiptIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {loading ? (
            <Typography>Loading invoice details...</Typography>
          ) : invoice ? (
            <Box>
              {/* Invoice Summary Card */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>Invoice Summary</Typography>
                      <Typography><strong>Invoice Number:</strong> {getInvoiceNumber(invoice.invoice_id)}</Typography>
                      <Typography>
                        <strong>Project:</strong> {
                          invoice.project?.project_title || 
                          invoice.projectInfo?.project_title || 
                          (typeof invoice.project_id === 'string' ? invoice.project_id : invoice.project_id?.project_title) || 
                          'N/A'
                        }
                      </Typography>
                      <Typography>
                        <strong>Generation Date:</strong> {new Date(invoice.generation_date).toLocaleDateString()}
                      </Typography>
                      <Typography>
                        <strong>Billing Period:</strong> {
                          invoice.billing_period?.start_date && invoice.billing_period?.end_date
                            ? `${fmtUTCDate(invoice.billing_period.start_date)} - ${fmtUTCDate(invoice.billing_period.end_date)}`
                            : invoice.bill_start_date && invoice.bill_end_date
                              ? `${fmtUTCDate(invoice.bill_start_date)} - ${fmtUTCDate(invoice.bill_end_date)}`
                              : 'N/A'
                        }
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6">Status</Typography>
                        <Chip 
                          label={getCurrentStatus()} 
                          color={getStatusColor(getCurrentStatus())}
                        />
                      </Box>
                      {/* Show current period amount (this invoice's charges only) */}
                      <Typography>
                        <strong>This Invoice Amount:</strong> ${(invoice.current_period_amount || invoice.total_amount)?.toFixed(2)}
                      </Typography>
                      {/* Show previous balance if any */}
                      {invoice.previous_balance && invoice.previous_balance.length > 0 && (
                        <Typography color="text.secondary">
                          <strong>+ Previous Balance:</strong> ${invoice.previous_balance.reduce((sum, pb) => sum + (pb.remaining_amount || 0), 0).toFixed(2)}
                        </Typography>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle1">
                        <strong>Total Amount:</strong> ${invoice.total_amount?.toFixed(2)}
                      </Typography>
                      <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                      <Typography><strong>Amount Paid:</strong> ${invoice.amount_paid?.toFixed(2) || '0.00'}</Typography>
                      <Typography><strong>Remaining Balance:</strong> ${invoice.remaining_balance?.toFixed(2) || 
                        (invoice.total_amount - (invoice.amount_paid || 0)).toFixed(2)}</Typography>
                      
                      {getCurrentStatus() !== 'Paid' && (
                        <Button 
                          variant="contained" 
                          color="primary" 
                          sx={{ mt: 2 }}
                          onClick={() => setRecordPaymentOpen(true)}
                        >
                          Record Payment
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Tabs for different sections */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Compartment Usage" />
                  <Tab label="Resource Usage" />
                  <Tab label="Previous Balance" />
                  <Tab label="Payments" />
                  <Tab label="Status History" />
                </Tabs>
              </Box>

              {/* Chamber Usage Tab */}
              {tabValue === 0 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Compartment</TableCell>
                        <TableCell>Unit Type</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Days Used</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Total Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.chamber_usage?.length > 0 ? (
                        invoice.chamber_usage.map((usage, index) => {
                          // Extract chamber name
                          let chamberName = 'N/A';
                          if (usage.chamber) {
                            if (typeof usage.chamber === 'object') {
                              chamberName = usage.chamber.chamber || 'N/A';
                            } else {
                              chamberName = usage.chamber;
                            }
                          }

                          // Extract unit details
                          let unitType = 'N/A';
                          let rateName = '';
                          let quantity = 0;
                          let rate = 0;
                          let totalCost = 0;
                          let startDate = null;
                          let endDate = null;
                          let storedDays = null;

                          // Handle both new schema (assignments) and old schema (units)
                          const assignmentsOrUnits = usage.assignments || usage.units || [];
                          if (assignmentsOrUnits.length > 0) {
                            const item = assignmentsOrUnits[0]; // Get the first item
                            if (item.total_days != null) storedDays = item.total_days;
                            // New schema uses rate_name/rate_id, old schema uses unit object
                            if (item.unit) {
                              if (typeof item.unit === 'object') {
                                unitType = item.unit.base_unit || item.unit.unit || 'N/A';
                              } else {
                                unitType = item.unit;
                              }
                            }
                            if (item.rate_name) {
                              rateName = item.rate_name;
                              rate = item.rate || 0;
                            } else if (item.rate_id) {
                              rateName = item.rate_id;
                              rate = item.rate || 0;
                            } else if (item.unit && typeof item.unit === 'object') {
                              rate = item.unit.rate || 0;
                            }
                            quantity = item.quantity || 0;
                            totalCost = item.total_cost || 0;
                            startDate = item.start_date ? new Date(item.start_date) : null;
                            endDate = item.end_date ? new Date(item.end_date) : null;
                          }

                          // Prefer the stored (billed) day count so the display
                          // always matches what the client was actually charged.
                          // Fall back to the shared inclusive, DST-safe calc for
                          // older records that lack a stored total_days.
                          const daysUsed = storedDays != null
                            ? storedDays
                            : (startDate && endDate ? calculateDays(startDate, endDate) : 0);

                          return (
                            <TableRow key={index}>
                              <TableCell>{chamberName}</TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">{unitType}</Typography>
                                  {rateName && <Typography variant="caption" color="textSecondary">{rateName}</Typography>}
                                </Box>
                              </TableCell>
                              <TableCell>{quantity}</TableCell>
                              <TableCell>{startDate ? fmtUTCDate(startDate) : 'N/A'}</TableCell>
                              <TableCell>{endDate ? fmtUTCDate(endDate) : 'N/A'}</TableCell>
                              <TableCell>{daysUsed}</TableCell>
                              <TableCell>
                                {formatRate(rate)}
                                {' '}{formatRateBasis(assignmentsOrUnits[0]?.rate_basis)}
                              </TableCell>
                              <TableCell>${totalCost.toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center">No compartment usage data</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell colSpan={7} align="right"><strong>Total Compartment Cost:</strong></TableCell>
                        <TableCell>
                          <strong>
                            ${invoice.chamber_usage?.reduce((sum, item) => {
                              // Handle both new schema (assignments) and old schema (units)
                              const assignmentsOrUnits = item.assignments || item.units || [];
                              const itemTotal = assignmentsOrUnits[0]?.total_cost || 0;
                              return sum + itemTotal;
                            }, 0).toFixed(2) || '0.00'}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Resource Usage Tab */}
              {tabValue === 1 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Resource Name</TableCell>
                        <TableCell>Rate Type</TableCell>
                        <TableCell>Units</TableCell>
                        <TableCell>Rate/Unit</TableCell>
                        <TableCell>Total Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.resource_usage?.length > 0 ? (
                        invoice.resource_usage.map((usage, index) => {
                          let resourceName = 'N/A';
                          if (usage.resource) {
                            if (typeof usage.resource === 'object') {
                              resourceName = usage.resource.resource || usage.resource.name || 'N/A';
                            } else {
                              resourceName = usage.resource;
                            }
                          } else if (usage.resource_id && typeof usage.resource_id === 'object') {
                            resourceName = usage.resource_id.resource || usage.resource_id.name || 'N/A';
                          }
                          const basis = usage.rate_basis || 'unit';
                          const unitsDisplay = usage.quantity != null ? `${usage.quantity} (${basis})` : '—';
                          return (
                            <TableRow key={index}>
                              <TableCell>{resourceName}</TableCell>
                              <TableCell>{usage.rate_display_name || '—'}</TableCell>
                              <TableCell>{unitsDisplay}</TableCell>
                              <TableCell>${(usage.rate || 0).toFixed(2)}</TableCell>
                              <TableCell>${(usage.total_cost || 0).toFixed(2)}</TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">No resource usage data</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell colSpan={4} align="right"><strong>Total:</strong></TableCell>
                        <TableCell>
                          <strong>
                            ${invoice.resource_usage?.reduce((sum, item) => sum + (item.total_cost || 0), 0).toFixed(2) || '0.00'}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Previous Balance Tab */}
              {tabValue === 2 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Original Amount</TableCell>
                        <TableCell>Amount Paid</TableCell>
                        <TableCell>Remaining Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.previous_balance?.length > 0 ? (
                        invoice.previous_balance.map((balance, index) => (
                          <TableRow key={index}>
                            <TableCell>{getInvoiceNumber(balance.invoice_id) || balance.invoice_number || 'N/A'}</TableCell>
                            <TableCell>${balance.original_amount?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell>${balance.amount_paid?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell>${balance.remaining_amount?.toFixed(2) || '0.00'}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No previous balance data</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell colSpan={3} align="right"><strong>Total:</strong></TableCell>
                        <TableCell>
                          <strong>
                            ${invoice.previous_balance?.reduce((sum, item) => sum + (item.remaining_amount || 0), 0).toFixed(2) || '0.00'}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Payments Tab */}
              {tabValue === 3 && (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Paid By</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Payment Mode</TableCell>
                        <TableCell>Receipt/Transaction ID</TableCell>
                        <TableCell>Comments</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.payments?.length > 0 ? (
                        invoice.payments.map((payment, index) => (
                          <TableRow key={index}>
                            <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              {getPaymentUserName(payment.paid_by)}
                            </TableCell>
                            <TableCell>${payment.amount?.toFixed(2) || '0.00'}</TableCell>
                            <TableCell>{payment.payment_mode}</TableCell>
                            <TableCell>
                              {payment.receipt_number || payment.transaction_id || 'N/A'}
                            </TableCell>
                            <TableCell>{payment.comments || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <IconButton aria-label="Edit Payment"
                                size="small"
                                onClick={() => handleEditPayment(payment)}
                                title="Edit Payment"
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton aria-label="Delete Payment"
                                size="small"
                                onClick={() => {
                                  setPaymentToDelete(payment);
                                  setDeletePaymentConfirmOpen(true);
                                }}
                                title="Delete Payment"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">No payment records</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell colSpan={2} align="right"><strong>Total Paid:</strong></TableCell>
                        <TableCell colSpan={5}>
                          <strong>${invoice.amount_paid?.toFixed(2) || '0.00'}</strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Status History Tab */}
              {tabValue === 4 && (
                <List component={Paper}>
                  {invoice.status_history?.length > 0 ? (
                    invoice.status_history.map((status, index) => (
                      <React.Fragment key={index}>
                        <ListItem alignItems="flex-start">
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="subtitle1">{status.status}</Typography>
                                <Typography variant="body2">
                                  {new Date(status.date).toLocaleString()}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" color="textSecondary">
                                  {status.comments || 'No comments'}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  Updated by: {getPaymentUserName(status.updated_by)}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                        {index < invoice.status_history.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="No status history available" />
                    </ListItem>
                  )}
                </List>
              )}
            </Box>
          ) : (
            <Typography>No invoice data available</Typography>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      {invoice && (
        <RecordPayment 
          open={recordPaymentOpen}
          onClose={() => setRecordPaymentOpen(false)}
          invoice={invoice}
          onPaymentRecorded={handlePaymentRecorded}
        />
      )}

      {/* Delete Payment Confirmation Dialog */}
      <Dialog
        open={deletePaymentConfirmOpen}
        onClose={() => {
          if (!isDeletingPayment) {
            setDeletePaymentConfirmOpen(false);
            setPaymentToDelete(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Payment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove the payment of{' '}
            <strong>${paymentToDelete?.amount?.toFixed(2) || '0.00'}</strong>
            {paymentToDelete?.payment_mode ? ` (${paymentToDelete.payment_mode})` : ''} from this invoice.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            The invoice balance and the User Accounts Report will be updated accordingly. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDeletePaymentConfirmOpen(false);
              setPaymentToDelete(null);
            }}
            disabled={isDeletingPayment}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeletePayment}
            disabled={isDeletingPayment}
            startIcon={<DeleteIcon />}
          >
            {isDeletingPayment ? 'Deleting...' : 'Delete Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Payment Dialog */}
      {invoice && selectedPayment && (
        <EditPayment
          open={editPaymentOpen}
          onClose={() => {
            setEditPaymentOpen(false);
            setSelectedPayment(null);
          }}
          invoice={invoice}
          payment={selectedPayment}
          onPaymentUpdated={handlePaymentUpdated}
        />
      )}
    </>
  );
};

export default InvoiceDetail; 